import { useApi } from '@backstage/core-plugin-api';
import { uptimeMonitorApiRef } from '../api/UptimeMonitorApi';
import { useCallback } from 'react';
import { useAsyncFn } from 'react-use';
import { IssueUpdateRequest, IssueStatus, StatusUpdateWithCommentRequest } from '../types';

export const useIssueActions = () => {
  const api = useApi(uptimeMonitorApiRef);

  const [updateState, updateIssueStatus] = useAsyncFn(
    async (params: IssueUpdateRequest) => {
      try {
        return await api.updateIssueStatus(params);
      } catch (err: any) {
        throw err;
      }
    },
    [],
  );

  const [updateWithCommentState, updateIssueStatusWithComment] = useAsyncFn(
    async (params: StatusUpdateWithCommentRequest) => {
      try {
        return await api.updateIssueStatusWithComment(params);
      } catch (err: any) {
        throw err;
      }
    },
    [],
  );

  const markAsResolvedWithComment = useCallback(
    (host: string, org: string, issueId: number, comment: string) => {
      return updateIssueStatusWithComment({
        host,
        org,
        issueId,
        status: IssueStatus.RESOLVED,
        comment,
      });
    },
    [updateIssueStatusWithComment],
  );

  const markAsIgnoredWithComment = useCallback(
    (host: string, org: string, issueId: number, comment: string) => {
      return updateIssueStatusWithComment({
        host,
        org,
        issueId,
        status: IssueStatus.IGNORED,
        comment,
      });
    },
    [updateIssueStatusWithComment],
  );

  const markAsUnresolvedWithComment = useCallback(
    (host: string, org: string, issueId: number, comment: string) => {
      return updateIssueStatusWithComment({
        host,
        org,
        issueId,
        status: IssueStatus.UNRESOLVED,
        comment,
      });
    },
    [updateIssueStatusWithComment],
  );

  const markAsResolved = useCallback(
    (host: string, org: string, issueIds: number[]) => {
      return updateIssueStatus({
        host,
        org,
        status: IssueStatus.RESOLVED,
        ...(issueIds.length === 1 ? { id: issueIds[0] } : { ids: issueIds }),
      });
    },
    [updateIssueStatus],
  );

  const markAsIgnored = useCallback(
    (host: string, org: string, issueIds: number[]) => {
      return updateIssueStatus({
        host,
        org,
        status: IssueStatus.IGNORED,
        ...(issueIds.length === 1 ? { id: issueIds[0] } : { ids: issueIds }),
      });
    },
    [updateIssueStatus],
  );

  const markAsUnresolved = useCallback(
    (host: string, org: string, issueIds: number[]) => {
      return updateIssueStatus({
        host,
        org,
        status: IssueStatus.UNRESOLVED,
        ...(issueIds.length === 1 ? { id: issueIds[0] } : { ids: issueIds }),
      });
    },
    [updateIssueStatus],
  );

  return {
    isUpdating: updateState.loading,
    updateError: updateState.error,
    isUpdatingWithComment: updateWithCommentState.loading,
    updateWithCommentError: updateWithCommentState.error,
    markAsResolvedWithComment,
    markAsIgnoredWithComment,
    markAsUnresolvedWithComment,
    markAsResolved,
    markAsIgnored,
    markAsUnresolved,
    updateIssueStatus,
  };
};